import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRadarToken, useRadarTokenAction } from "@/features/radar/hooks";
import { useAuth } from "@/auth/use-auth";

function dateAfter(days: number): string {
  const value = new Date();
  value.setDate(value.getDate() + days);
  return value.toISOString().slice(0, 10);
}

export function RadarPublicPage(): JSX.Element {
  const { token = "" } = useParams();
  const { auth, signOut } = useAuth();
  const navigate = useNavigate();
  const opportunity = useRadarToken(token);
  const action = useRadarTokenAction(token);
  const [mode, setMode] = useState<
    "idle" | "claim" | "pass" | "snooze" | "not_relevant" | "field_intel"
  >("idle");
  const [role, setRole] = useState("lead");
  const [reasonCode, setReasonCode] = useState("not_a_fit");
  const [reasonText, setReasonText] = useState("");
  const [colleagueEmail, setColleagueEmail] = useState("");
  const [snoozeUntil, setSnoozeUntil] = useState(() => dateAfter(14));
  const [done, setDone] = useState("");
  const [savedClaim, setSavedClaim] = useState<{
    pursuitId: string;
    activationQueued: boolean;
  } | null>(null);
  const minimumSnoozeDate = useMemo(() => dateAfter(1), []);
  const maximumSnoozeDate = useMemo(() => dateAfter(365), []);

  const submit = (nextAction: string, fields: Record<string, unknown> = {}) => {
    action.mutate(
      { action: nextAction, fields },
      {
        onSuccess: (result) => {
          const pursuitId = typeof result.pursuitId === "string" ? result.pursuitId : "";
          if (nextAction === "claim" || nextAction === "snooze") {
            const view = nextAction === "snooze" ? "follow_ups" : "claimed";
            const destination = new URLSearchParams({ view });
            if (pursuitId) destination.set("pursuit", pursuitId);
            if (nextAction === "claim" && opportunity.data?.access.state !== "active") {
              setSavedClaim({ pursuitId, activationQueued: false });
              setMode("idle");
              return;
            }
            navigate(`/radar?${destination.toString()}`);
            return;
          }
          const pending =
            result.leadRequestPending === true
              ? " Your request to become lead is pending resolution."
              : "";
          const forwarded =
            result.forwarded === true
              ? ` The opportunity was passed to ${String(result.colleague)}.`
              : "";
          setDone(
            `${nextAction === "claim" ? "Pursuit joined" : nextAction === "field_intel" ? "Field intelligence added" : "Response recorded"}.${pending}${forwarded}`
          );
          setMode("idle");
        },
      }
    );
  };

  if (opportunity.isPending)
    return (
      <main className="radar-public">
        <div className="radar-empty">Opening your private RADAR brief…</div>
      </main>
    );
  if (opportunity.isError || !opportunity.data)
    return (
      <main className="radar-public">
        <div className="radar-empty radar-error">
          <h1>This RADAR link is unavailable</h1>
          <p>It may have expired, been revoked, or already completed its purpose.</p>
        </div>
      </main>
    );
  const data = opportunity.data;
  const workspacePath = `/radar?view=opportunities&opportunity=${encodeURIComponent(
    data.opportunity.id
  )}`;
  const signedInAsRecipient =
    auth.status === "ready" &&
    auth.profile.email.trim().toLowerCase() === data.recipient.email.trim().toLowerCase();

  const requestAccess = () => {
    action.mutate(
      { action: "request_access" },
      {
        onSuccess: () => {
          setSavedClaim((current) => (current ? { ...current, activationQueued: true } : current));
        },
      }
    );
  };

  return (
    <main className="radar-public">
      <header className="radar-public-head">
        <img src="/radar-wordmark-glass.png" alt="RADAR by BD Signal Engine" />
        <div>
          <strong>{data.recipient.name}</strong>
          <span>{data.opportunity.practice.toUpperCase()} · private brief</span>
        </div>
      </header>
      <section className="radar-public-hero">
        <p className="radar-eyebrow">{data.opportunity.band.toUpperCase()} OPPORTUNITY</p>
        <h1>{data.opportunity.company}</h1>
        <p>{data.alert.summary}</p>
        <div className="radar-tags">
          <span>{data.opportunity.relationship}</span>
          {data.opportunity.industry ? <span>{data.opportunity.industry}</span> : null}
          {data.opportunity.geography ? <span>{data.opportunity.geography}</span> : null}
        </div>
      </section>
      <div className="radar-public-layout">
        <section className="radar-public-main">
          <article className="radar-card">
            <p className="radar-eyebrow">WHY THIS IS A RADAR SIGNAL</p>
            <div className="radar-proof-list">
              {data.proofs.map((proof) => (
                <div className="radar-proof-item" key={proof.key}>
                  <div className="radar-proof-equation">
                    {proof.url ? (
                      <a href={proof.url} target="_blank" rel="noreferrer">
                        {proof.label}
                        <span aria-hidden="true">↗</span>
                      </a>
                    ) : (
                      <strong>{proof.label}</strong>
                    )}
                    {(proof.suffixes ?? []).map((suffix) => (
                      <strong key={suffix}> + {suffix}</strong>
                    ))}
                  </div>
                  {proof.context ? <small>{proof.context}</small> : null}
                </div>
              ))}
            </div>
            <p className="radar-proof-context">
              {data.opportunity.reasons.join(" · ") || data.alert.summary}
            </p>
          </article>
          <article className="radar-card">
            <p className="radar-eyebrow">SUGGESTED FIRST MOVE</p>
            <h2>Start with context</h2>
            <p>{data.alert.firstMove}</p>
          </article>
          <article className="radar-card">
            <p className="radar-eyebrow">GOVERNED EVIDENCE</p>
            <h2>{data.opportunity.evidenceCount} evidence items</h2>
            {data.evidence.length ? (
              data.evidence.map((item) => (
                <div className="radar-evidence" key={item.id}>
                  <strong>{item.title}</strong>
                  <span>
                    {item.source} · {new Date(item.observedAt).toLocaleDateString()}
                  </span>
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noreferrer">
                      {item.isBdseSignal ? "Open BDSE signal" : "Open source"}
                    </a>
                  ) : null}
                </div>
              ))
            ) : (
              <p>BDSE source evidence is retained; no external report excerpt is displayed here.</p>
            )}
          </article>
        </section>
        <aside className="radar-action-card">
          <p className="radar-eyebrow">YOUR RESPONSE</p>
          <h2>What should happen next?</h2>
          {data.access.state === "active" ? (
            <div className="radar-access-choice">
              <strong>
                {signedInAsRecipient ? "Continue in BDSE" : "Already have BDSE access?"}
              </strong>
              <p>
                Open this exact alert in My RADAR. Your other assigned opportunities will be there
                too.
              </p>
              {auth.status === "ready" && !signedInAsRecipient ? (
                <button
                  type="button"
                  className="primary radar-link-button"
                  onClick={() =>
                    void signOut().then(() =>
                      navigate("/login", { replace: true, state: { from: workspacePath } })
                    )
                  }
                >
                  Sign out and switch account
                </button>
              ) : (
                <Link
                  className="primary radar-link-button"
                  to={signedInAsRecipient ? workspacePath : "/login"}
                  state={signedInAsRecipient ? undefined : { from: workspacePath }}
                >
                  {signedInAsRecipient ? "Open this alert in My RADAR" : "Sign in to BDSE"}
                </Link>
              )}
            </div>
          ) : !savedClaim ? (
            <div className="radar-access-choice">
              <strong>New to BDSE?</strong>
              <p>
                Set up a new account from this private alert. You will confirm the claim first so
                the opportunity is saved, then receive a secure account-setup email and return
                directly to this alert.
              </p>
              <button
                type="button"
                className="primary radar-link-button"
                onClick={() => setMode("claim")}
              >
                Set up new BDSE account
              </button>
            </div>
          ) : null}
          {savedClaim ? (
            <div className="radar-access-success" role="status">
              <span className="radar-access-success-icon" aria-hidden="true">
                ✓
              </span>
              <h3>Your claim is saved</h3>
              <p>
                This opportunity is safely assigned to you. Activate BDSE access to open it in My
                RADAR and continue from the same pursuit.
              </p>
              {savedClaim.activationQueued ? (
                <div className="radar-access-email-sent">
                  <strong>Activation email sent</strong>
                  <span>Check {data.recipient.email} for your secure setup link.</span>
                </div>
              ) : (
                <button
                  type="button"
                  className="primary"
                  disabled={action.isPending}
                  onClick={requestAccess}
                >
                  {action.isPending ? "Preparing access…" : "Set up BDSE account"}
                </button>
              )}
              <small>Your claim stays saved even if you finish activation later.</small>
            </div>
          ) : null}
          {done ? (
            <p className="radar-success" role="status">
              {done}
            </p>
          ) : null}
          {mode === "idle" && !savedClaim ? (
            <div className="radar-actions">
              {data.access.state === "active" ? (
                <button type="button" className="primary" onClick={() => setMode("claim")}>
                  Claim or join
                </button>
              ) : null}
              <button type="button" onClick={() => setMode("pass")}>
                Pass with reason
              </button>
              <button type="button" onClick={() => setMode("snooze")}>
                Snooze
              </button>
              <button type="button" onClick={() => setMode("not_relevant")}>
                Not relevant
              </button>
              <button type="button" onClick={() => setMode("field_intel")}>
                Add field intelligence
              </button>
            </div>
          ) : null}
          {mode === "claim" ? (
            <div className="radar-confirm">
              <label>
                Your role
                <select value={role} onChange={(event) => setRole(event.target.value)}>
                  <option value="lead">Lead</option>
                  <option value="support">Support</option>
                  <option value="intro">Introduction</option>
                </select>
              </label>
              <p>Claiming creates or joins the one shared pursuit for this company.</p>
              <button
                type="button"
                className="primary"
                disabled={action.isPending}
                onClick={() => submit("claim", { role, reasonText })}
              >
                Confirm claim
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {mode === "pass" || mode === "not_relevant" ? (
            <div className="radar-confirm">
              <label>
                Reason
                <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}>
                  <option value="not_a_fit">Not a fit</option>
                  <option value="at_capacity">At capacity</option>
                  <option value="on_pto">Going on PTO</option>
                  <option value="wrong_practice">Wrong practice</option>
                  <option value="other">Other</option>
                </select>
              </label>
              {mode === "pass" ? (
                <label>
                  Pass to an active BDSE colleague (optional)
                  <input
                    type="email"
                    value={colleagueEmail}
                    onChange={(event) => setColleagueEmail(event.target.value)}
                    placeholder="colleague@ankura.com"
                  />
                </label>
              ) : null}
              <label>
                Optional context
                <textarea
                  value={reasonText}
                  onChange={(event) => setReasonText(event.target.value)}
                />
              </label>
              <button
                type="button"
                className="primary"
                disabled={action.isPending}
                onClick={() => submit(mode, { reasonCode, reasonText, colleagueEmail })}
              >
                Confirm response
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {mode === "snooze" ? (
            <div className="radar-confirm">
              <label>
                Follow up on
                <input
                  type="date"
                  min={minimumSnoozeDate}
                  max={maximumSnoozeDate}
                  value={snoozeUntil}
                  onChange={(event) => setSnoozeUntil(event.target.value)}
                />
              </label>
              <p>RADAR will place this opportunity in Follow Ups until the date you choose.</p>
              <button
                type="button"
                className="primary"
                disabled={action.isPending || !snoozeUntil}
                onClick={() => submit("snooze", { until: `${snoozeUntil}T12:00:00Z` })}
              >
                Set follow-up
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {mode === "field_intel" ? (
            <div className="radar-confirm">
              <label>
                What do you know?
                <textarea
                  value={reasonText}
                  onChange={(event) => setReasonText(event.target.value)}
                  placeholder="Add first-hand account context, timing, stakeholders, or a specific next move."
                />
              </label>
              <button
                type="button"
                className="primary"
                disabled={action.isPending || !reasonText.trim()}
                onClick={() =>
                  submit("field_intel", { reasonCode: "recipient_observation", reasonText })
                }
              >
                Add governed evidence
              </button>
              <button type="button" onClick={() => setMode("idle")}>
                Cancel
              </button>
            </div>
          ) : null}
          {action.isError ? (
            <p className="radar-error" role="alert">
              The response could not be saved. Please try again.
            </p>
          ) : null}
          <small>
            Expires {new Date(data.expiresAt).toLocaleDateString()}. Opening this page never claims
            the opportunity.
          </small>
        </aside>
      </div>
    </main>
  );
}
